import { Badge, ChatSidebarItem } from '@wundo/ui';
import type { UserOverview } from '@/types/messages.ts';
import { attentionOf } from '@/components/conversationReview/attentionOf.ts';
import { ATTENTION_TONES } from '@/components/conversationReview/needsAttention.ts';
import { SEVERITY_LABELS } from '@/components/conversationReview/severity.ts';
import { formatMessageDay } from '@/utils/messageUtils.ts';
import { personName } from './personName.ts';

export interface PersonRowProps {
  row: UserOverview;
  current: boolean;
  onSelect: () => void;
}

/** One person: their name and how many findings wait, then where they are and how much they said. */
export function PersonRow({ row, current, onSelect }: PersonRowProps) {
  const attention = attentionOf(row.open_finding_severities);
  const findings = attention
    ? `${attention.count} open ${attention.count === 1 ? 'finding' : 'findings'}, worst: ${SEVERITY_LABELS[attention.severity]}`
    : null;

  return (
    <ChatSidebarItem
      data-ui='person-row'
      preview={personName(row)}
      previewLines={1}
      when={[
        row.organisation.name ?? '—',
        `${row.messageCount} ${row.messageCount === 1 ? 'message' : 'messages'}`,
        row.lastMessageDate ? formatMessageDay(row.lastMessageDate) : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      current={current}
      onClick={onSelect}
      badge={
        attention && findings ? (
          <Badge
            data-ui='findings-badge'
            tone={ATTENTION_TONES[attention.severity]}
            title={findings}
            className='relative min-w-5.5 justify-center tabular-nums'
          >
            {attention.count}
            <span className='sr-only'>{` open ${attention.count === 1 ? 'finding' : 'findings'}`}</span>
          </Badge>
        ) : null
      }
    />
  );
}
